// Consent handling.
//
// Preferred: Google's certified consent tool (configured in AdSense > Privacy & messaging),
// which is injected by the AdSense script and exposes the IAB TCF API (window.__tcfapi).
// Google only serves personalised ads to UK/EEA visitors when a certified CMP is used.
//
// Fallback: if no TCF consent tool appears (e.g. before it is switched on in AdSense),
// the site's own cookie banner (components/CookiePopup) is shown, as before.

import cookies from "browser-cookies";
import { COOKIES_ENABLED } from "./constants";
import { isEmbedded } from "./portal";

declare global {
  interface Window {
    __tcfapi?: (command: string, version: number, callback: (data: any, success: boolean) => void, param?: any) => void;
  }
}

const CMP_WAIT_MS = 2500;

// Resolves true once a TCF consent tool is present on the page, false after a short wait.
export const waitForCmp = (): Promise<boolean> =>
  new Promise((resolve) => {
    if (typeof window === "undefined") return resolve(false);
    if (typeof window.__tcfapi === "function") return resolve(true);
    const started = Date.now();
    const timer = setInterval(() => {
      if (typeof window.__tcfapi === "function") {
        clearInterval(timer);
        resolve(true);
      } else if (Date.now() - started > CMP_WAIT_MS) {
        clearInterval(timer);
        resolve(false);
      }
    }, 100);
  });

type Listener = (granted: boolean) => void;
const listeners: Listener[] = [];
let lastKnown: boolean | undefined;

const notify = (granted: boolean) => {
  if (lastKnown === granted) return;
  lastKnown = granted;
  listeners.forEach((l) => l(granted));
};

// "Storage" consent = TCF purpose 1 (store and/or access information on a device),
// or GDPR not applying to this visitor (e.g. outside the UK/EEA).
const purposeOneGranted = (tcData: any) =>
  tcData && (tcData.gdprApplies === false || Boolean(tcData.purpose?.consents?.[1]));

let started = false;
export const startConsentTracking = async () => {
  if (started || isEmbedded) return; // portals handle consent; analytics stays off
  // Automated browsers (our own test runs, portals' review bots) aren't players: keep them
  // out of the analytics, which otherwise show hundreds of "users" with zero engagement.
  if ((navigator as any).webdriver) return;
  started = true;
  if (cookies.get(COOKIES_ENABLED)) notify(true); // accepted via the old banner previously
  const hasCmp = await waitForCmp();
  if (!hasCmp || !window.__tcfapi) return;
  window.__tcfapi("addEventListener", 2, (tcData, success) => {
    if (!success) return;
    if (["tcloaded", "useractioncomplete"].includes(tcData.eventStatus)) {
      notify(purposeOneGranted(tcData));
    }
  });
};

// Calls back with the current consent state now (if known) and whenever it changes.
export const onConsentChange = (listener: Listener) => {
  listeners.push(listener);
  if (lastKnown !== undefined) listener(lastKnown);
};

// Used by the fallback banner. A refusal is remembered (no consent is granted).
export const COOKIES_REJECTED = "cookies-rejected";
export const rejectFallbackConsent = () => {
  cookies.set(COOKIES_REJECTED, "true", { expires: 365 });
  notify(false);
};
export const fallbackChoiceMade = () => Boolean(cookies.get(COOKIES_ENABLED) || cookies.get(COOKIES_REJECTED));

export const grantFallbackConsent = () => {
  cookies.set(COOKIES_ENABLED, "true", { expires: 365 * 10 });
  notify(true);
};

// Re-open the consent choices ("Privacy and cookie settings" link).
// App: AdMob's privacy options form. Website: Google's consent tool, or the site's own banner as a fallback.
export const openPrivacySettings = () => {
  const w = window as any;
  const admob = w.Capacitor?.isNativePlatform?.() && w.Capacitor?.Plugins?.AdMob;
  if (admob) {
    admob.showPrivacyOptionsForm?.().catch?.(() => undefined);
    return;
  }
  // Our own cookie box: forget the earlier choice and show the box again straight away.
  const ownBox = () => {
    cookies.erase(COOKIES_ENABLED);
    cookies.erase(COOKIES_REJECTED);
    window.dispatchEvent(new Event("rafiki:cookie-choices"));
  };
  if (typeof w.__tcfapi === "function") {
    w.googlefc = w.googlefc || {};
    w.googlefc.callbackQueue = w.googlefc.callbackQueue || [];
    let shown = false;
    w.googlefc.callbackQueue.push(() => {
      if (typeof w.googlefc.showRevocationMessage === "function") {
        w.googlefc.showRevocationMessage();
        shown = true;
      }
    });
    // Google's tool only offers this in some countries, and can be blocked: if its window
    // hasn't appeared shortly, show our own cookie box so the link always does something.
    setTimeout(() => {
      const googleWindow = document.querySelector(".fc-consent-root, .fc-dialog-container, .fc-message-root");
      if (!shown || !googleWindow) ownBox();
    }, 1500);
    return;
  }
  ownBox();
};
